import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { getTaxesInspect } from 'Services/invoices';
import Spinner from 'Components/Atoms/Spinner';
import { formatCurrency } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';
import useCurrency from 'Hooks/useCurrency';
import useDrawerStack from 'Hooks/useDrawerStack';
import { DRAWER_STACK, RESULTS_PER_PAGE } from 'Utils/constants';
import InvoiceTitle from 'Components/Atoms/InvoiceTitle/InvoiceTitle';
import Icon from 'Components/Atoms/Icon/Icon';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';
import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineArrowBottom from 'Components/Atoms/OutlineIcons/OutlineArrowBottom';
import classes from 'Pages/Calendar/Calendar.module.css';

export default function TaxesCellDetailDrawer(props) {
    const { cellData } = props;
    const { t } = useTranslation('taxes');
    const locale = useLocale();
    const currency = useCurrency();
    const { open } = useDrawerStack();
    const sentinelRef = useRef(null);

    const getAmountMapping = () => {
        const amountType = cellData?.amountType;

        switch (amountType) {
            case 'amount_subtotal':
                return { amount: 'amount_subtotal', amountTotal: 'amount' };
            case 'amount':
                return { amount: 'amount_subtotal', amountTotal: 'amount' };
            case 'amount_tax_unassigned':
                return { amount: 'amount_tax_unassigned', amountTotal: 'amount' };
            case 'amount_reimbursable':
                return { amount: 'amount_reimbursable', amountTotal: 'amount' };
            default:
                return { amount: 'amount_tax', amountTotal: 'amount_subtotal' };
        }
    };

    const getCardLabels = () => {
        const amountType = cellData?.amountType;
        const rowLabel = cellData?.rowLabel || '-';

        if (amountType === 'amount_tax_unassigned') {
            return {
                leftLabel: t('drawer.unassignedTaxes'),
                rightLabel: t('drawer.totalTaxes'),
            };
        }

        if (amountType === 'amount_reimbursable') {
            return {
                leftLabel: t('drawer.reimbursables'),
                rightLabel: t('drawer.total'),
            };
        }

        if (cellData?.group) {
            return {
                leftLabel: rowLabel,
                rightLabel: t('drawer.taxBaseOf', { taxName: rowLabel }),
            };
        }

        return {
            leftLabel: t('drawer.taxBase'),
            rightLabel: t('drawer.total'),
        };
    };

    const { data, isFetching, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery(
        ['invoices', 'taxes-inspect', cellData],
        async ({ pageParam = 1 }) => {
            const { amount, amountTotal } = getAmountMapping();
            const response = await getTaxesInspect({
                type: cellData.type,
                dateFrom: cellData.date_from,
                dateTo: cellData.date_to,
                taxGroup: cellData.group,
                taxId: cellData.tax_id,
                amount,
                amountTotal,
                page: pageParam,
            });
            return response;
        },
        {
            enabled: Boolean(cellData?.type && cellData?.date_from && cellData?.date_to),
            refetchOnWindowFocus: false,
            cacheTime: 15 * 1000,
            getNextPageParam: (lastPage, allPages) => {
                const pagesLoaded = allPages.length;
                const totalPages = Math.ceil(lastPage.count / RESULTS_PER_PAGE);
                return pagesLoaded < totalPages ? pagesLoaded + 1 : undefined;
            },
        },
    );

    const items =
        data?.pages?.reduce((acc, page) => {
            return [...acc, ...(page.items || [])];
        }, []) ?? [];

    const totalAmount = data?.pages?.[0]?.amount ?? 0;
    const totalAmountTotal = data?.pages?.[0]?.amount_total ?? 0;

    const isSales = cellData?.type === 'sale';

    const { leftLabel, rightLabel } = getCardLabels();

    useEffect(() => {
        if (isFetching) return;

        if (sentinelRef.current) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        fetchNextPage();
                    }
                });
            });

            observer.observe(sentinelRef.current);

            return () => {
                observer.disconnect();
            };
        }
    }, [sentinelRef, isFetching, fetchNextPage]);

    const formatAmount = (amount, itemCurrency = currency) =>
        formatCurrency(amount, { locale, currency: itemCurrency });

    const openInvoice = (item) => {
        open(DRAWER_STACK.INVOICE_QUICK_EDITOR, {
            invoiceId: item.invoice_id,
            title: <InvoiceTitle invoice={item} />,
            drawerStackProps: {
                size: 'lg',
            },
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="py-6">
            <div className={classes.cardScroll}>
                <div className={classes.cardGroup}>
                    <div className={classes.card}>
                        <div className={classes.cardTitle}>{leftLabel}</div>
                        <div className={classes.cardAmount}>{formatAmount(totalAmount)}</div>
                    </div>

                    <div className={classes.card}>
                        <div className={classes.cardTitle}>{rightLabel}</div>
                        <div className={clsx(classes.cardAmount, isSales ? classes.income : classes.expenses)}>
                            {formatAmount(totalAmountTotal)}
                        </div>
                    </div>
                </div>
            </div>

            {items.length === 0 ? (
                <div className="py-5 text-center text-gray-500">{t('drawer.noItems')}</div>
            ) : (
                <>
                    <div className="bg-white rounded-md shadow-card">
                        {items.map((item) => (
                            <div key={item.invoice_id} className="px-5 py-3 not-last:border-b border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="grid grid-cols-[1fr_auto_32px] gap-3 grow items-center min-w-0 text-sm">
                                        <div className="truncate">{item.label || '-'}</div>

                                        <div className="font-mono text-right">
                                            <span className="font-medium text-dark-blue">
                                                {formatAmount(item.amount, item.currency)}
                                            </span>
                                            <span className="text-gray-400 text-xs">
                                                {' '}
                                                / {formatAmount(item.amount_total, item.currency)}
                                            </span>
                                        </div>

                                        <div className="flex relative">
                                            <Tooltip label={t('drawer.viewInvoice')}>
                                                <button
                                                    className="inline-flex relative"
                                                    type="button"
                                                    onClick={() => openInvoice(item)}
                                                >
                                                    <Icon name="nav-invoices" className="w-6 block shrink-0" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="mt-6 text-center text-gray-500 text-xs">
                            {hasNextPage &&
                                (isFetching ? (
                                    <div className="flex justify-center">
                                        <Spinner />
                                    </div>
                                ) : (
                                    <IconButton variant="ghost" icon={<OutlineArrowBottom />} onClick={fetchNextPage} />
                                ))}
                        </div>

                        {hasNextPage && <div ref={sentinelRef} />}
                    </div>
                </>
            )}
        </div>
    );
}

TaxesCellDetailDrawer.propTypes = {
    cellData: PropTypes.shape({
        type: PropTypes.string,
        amount: PropTypes.number,
        group: PropTypes.string,
        tax_id: PropTypes.string,
        date_from: PropTypes.string,
        date_to: PropTypes.string,
        amountType: PropTypes.oneOf(['amount', 'amount_subtotal', 'amount_tax', 'amount_tax_unassigned']),
        rowLabel: PropTypes.string,
    }),
};

TaxesCellDetailDrawer.drawerStackProps = {
    size: 'lg',
};
